import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import UserContainer from "@utils/state/userContainer";
import UserService from "@utils/apis/UserService";
import { RoseUserSettings } from "@types";
import { Theme } from "@theme";

export type UseUserSettingsResult = {
  changeSettings: (
    settings: FormData,
    callback?: (settings: RoseUserSettings) => void,
  ) => void;
  settings: RoseUserSettings;
};

export function useUserSettings(): UseUserSettingsResult {
  const { user, setUser } = UserContainer.useContainer();

  const mutation = useMutation<RoseUserSettings, AxiosError, FormData>(
    (settings) => UserService.changeSettings(settings),
    {
      onSuccess: async (newSettings) => {
        setUser({
          ...user,
          settings: newSettings,
        });
      },
    },
  );

  return {
    changeSettings: (
      formData: FormData,
      callback?: (settings: RoseUserSettings) => void,
    ) => {
      // Optimistic update: derive partial settings from the FormData (theme & watermark keys)
      if (user) {
        const baseSettings: RoseUserSettings = { ...user.settings };
        const partial: Partial<RoseUserSettings> = {};
        for (const [key, value] of formData.entries()) {
          if (key === "theme" && typeof value === "string") {
            partial.theme = value as RoseUserSettings["theme"];
          } else if (/_watermark$/.test(key)) {
            if (typeof value === "string") {
              // Empty string means remove watermark
              if (value === "") {
                delete (baseSettings as any)[key];
              } else {
                (partial as any)[key] = value;
              }
            } else if (value instanceof File) {
              // We let the component manage the local preview (object URL),
              // backend will return the persisted reference later.
              // Could store a temporary placeholder if needed.
            }
          }
        }
        setUser({
          ...user,
          settings: {
            ...baseSettings,
            ...partial,
          },
        });
      }
      mutation.mutate(formData, {
        onSettled: (newSettings) => {
          if (callback) {
            callback?.(newSettings);
          }
        },
      });
    },
    settings: user?.settings ?? { theme: Theme.getSystemTheme() },
  };
}
